/* ==========================================================
   ESSENCE STRICT SEARCH V2 — PRECISION LAYER
   Chez Mishel / Shopify Prestige

   Purpose:
   - Preserve the buyer-facing query and URL exactly as typed.
   - Keep one-word text searches broad across title + tag.
   - For multi-word text searches, require either:
       1) every term in the product title, or
       2) the full normalized phrase in a product tag.
   - Preserve native numeric / SKU-like search behavior.
   - Keep the existing Strict Search V1 render, sorting and
     infinite-scroll architecture intact.
========================================================== */

(() => {
  'use strict';

  if (window.__essenceStrictSearchV2Loaded) {
    return;
  }

  window.__essenceStrictSearchV2Loaded = true;

  const SEARCH_PATH_RE = /\/search\/?$/;
  const INTERNAL_SECTION_PARAM = 'section_id';
  const MAX_QUERY_LENGTH = 180;
  const MAX_TERMS = 12;

  const normalizeWhitespace = (value) =>
    String(value || '')
      .replace(/\s+/gu, ' ')
      .trim();

  const isSkuLikeQuery = (query) => {
    const normalized = normalizeWhitespace(query);

    return (
      normalized.length > 0 &&
      /\d/u.test(normalized) &&
      /^[\d\s._\-/]+$/u.test(normalized)
    );
  };

  const escapeSearchPhrase = (value) =>
    value
      .replace(/\\/gu, '\\\\')
      .replace(/"/gu, '\\"');

  const tokenizeTextQuery = (query) => {
    const normalized = normalizeWhitespace(query).slice(0, MAX_QUERY_LENGTH);

    if (!normalized) {
      return [];
    }

    return normalized
      .split(' ')
      .map((term) =>
        term
          .replace(/^[,.;!?()[\]{}<>]+/gu, '')
          .replace(/[,.;!?()[\]{}<>]+$/gu, '')
          .trim()
      )
      .filter(Boolean)
      .slice(0, MAX_TERMS);
  };

  const buildStrictQuery = (query) => {
    const terms = tokenizeTextQuery(query);

    if (!terms.length) {
      return '';
    }

    if (terms.length === 1) {
      const phrase = escapeSearchPhrase(terms[0]);
      return `(title:"${phrase}" OR tag:"${phrase}")`;
    }

    const titleQuery = terms
      .map((term) => `title:"${escapeSearchPhrase(term)}"`)
      .join(' AND ');

    const tagPhrase = escapeSearchPhrase(terms.join(' '));

    return `((${titleQuery}) OR tag:"${tagPhrase}")`;
  };

  const shouldUseStrictSearch = (query) =>
    Boolean(query) && !isSkuLikeQuery(query) && Boolean(buildStrictQuery(query));

  const getBuyerQuery = () => {
    try {
      return normalizeWhitespace(
        new URL(window.location.href).searchParams.get('q')
      );
    } catch (error) {
      return '';
    }
  };

  const replacePublicApi = () => {
    const currentApi = window.EssenceStrictSearch;

    if (!currentApi) {
      return;
    }

    window.EssenceStrictSearch = Object.freeze({
      ...currentApi,
      version: '2.0.0',
      normalizeWhitespace,
      isSkuLikeQuery,
      tokenizeTextQuery,
      buildStrictQuery,
      shouldUseStrictSearch
    });
  };

  const nativeFetch = window.fetch.bind(window);

  window.fetch = (input, init) => {
    try {
      const inputUrl =
        input instanceof Request
          ? input.url
          : input instanceof URL
            ? input.toString()
            : String(input);

      const url = new URL(inputUrl, window.location.href);

      if (
        url.origin === window.location.origin &&
        SEARCH_PATH_RE.test(url.pathname) &&
        url.searchParams.has(INTERNAL_SECTION_PARAM)
      ) {
        const buyerQuery = getBuyerQuery();

        if (shouldUseStrictSearch(buyerQuery)) {
          url.searchParams.set('q', buildStrictQuery(buyerQuery));

          if (input instanceof Request) {
            input = new Request(url.toString(), input);
          } else {
            input = url.toString();
          }
        }
      }
    } catch (error) {
      // Fail open: keep the request generated by Strict Search V1.
    }

    return nativeFetch(input, init);
  };

  replacePublicApi();

  // Strict Search V1 can start its first render before this deferred file
  // executes. Re-render once through the V2 request guard so the preview
  // always settles on the V2 query semantics.
  if (
    SEARCH_PATH_RE.test(window.location.pathname) &&
    window.EssenceStrictSearch &&
    typeof window.EssenceStrictSearch.render === 'function'
  ) {
    void window.EssenceStrictSearch.render();
  }
})();
